import { Popover } from '@scholastic/mockingjay';
import { LoadingBar } from '@data/components/basic/LoadingBar/LoadingBar';
import { cn } from '@data/utils/cn/cn';
import { formatMetric } from '@data/utils/format-metric';
import type { MetricFormat } from '@data/config/metrics';
import InfoIcon from '@data/assets/info-icon.svg';
import { formatAssessmentDate } from '@data/utils/format-assessment-date';

export type MetricCardVariant = 'primary' | 'secondary';

interface MetricCardProps {
  value: string | null;
  label: string;
  popoverContent?: string;
  className?: string;
  isLoading?: boolean;
  variant?: MetricCardVariant;
  format?: MetricFormat;
  dateOfAssessment?: string;
}

export function MetricCard({
  value,
  label,
  popoverContent,
  className,
  isLoading,
  variant = 'primary',
  format = 'number',
  dateOfAssessment,
}: MetricCardProps) {
  const formatted = formatMetric(value, format);
  return (
    <div
      // Read as one group, e.g. "Texts Completed: 142".
      role={isLoading ? undefined : 'group'}
      aria-label={isLoading ? undefined : `${label}: ${formatted}`}
      className={cn(
        'text-center',
        variant === 'primary' &&
          'flex min-h-[96px] flex-col justify-center rounded-lg border border-neutral-200 p-2',
        variant === 'primary' && (isLoading ? 'bg-white' : 'bg-purple-50'),
        variant === 'secondary' && 'mb-4 p-2 @[523px]:mb-0',
        className
      )}
    >
      {isLoading ? (
        <div
          role="status"
          aria-busy="true"
          aria-label="Loading metric"
          className="flex flex-col items-center gap-4"
        >
          <LoadingBar className="mx-0 w-[67px] self-center" />
          <LoadingBar />
        </div>
      ) : (
        <>
          {variant === 'primary' &&
            dateOfAssessment &&
            value &&
            value !== 'null' && (
              <div className="text-xs text-neutral-700">
                As of {formatAssessmentDate(dateOfAssessment)} Assessment
              </div>
            )}
          <div
            className={cn(
              'whitespace-nowrap text-3xl font-semibold',
              variant === 'secondary' && 'text-blue-700'
            )}
          >
            {formatted}
          </div>
          <div className="mx-auto line-clamp-2 max-w-[250px] text-center text-sm leading-6">
            {label}
            {popoverContent && (
              <Popover
                trigger={
                  <button
                    aria-label={`More information about ${label}`}
                    type="button"
                    className="ml-1 inline-flex translate-y-[-1px] align-middle"
                  >
                    <InfoIcon aria-hidden="true" fill="white" />
                  </button>
                }
              >
                {popoverContent}
              </Popover>
            )}
          </div>
        </>
      )}
    </div>
  );
}
